import { useState } from 'react'
import { Link } from 'react-router'

function EmailCapture() {
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<'childminder' | 'parent' | ''>('')
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle')
  const [message, setMessage] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email) return

    setStatus('loading')
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL || ''}/api/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, role: role || undefined, source: 'homepage' }),
      })

      const data = await response.json()

      if (response.ok) {
        setStatus('success')
        setMessage('You\'re on the list! We\'ll be in touch soon.')
        setEmail('')
        setRole('')
      } else {
        setStatus('error')
        setMessage(data.error || 'Something went wrong. Please try again.')
      }
    } catch {
      setStatus('error')
      setMessage('Something went wrong. Please try again.')
    }
  }

  if (status === 'success') {
    return (
      <div className="bg-green-50 rounded-2xl p-8 text-center">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h3 className="text-xl font-bold text-gray-900 mb-2">You're on the list!</h3>
        <p className="text-gray-600">{message}</p>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label htmlFor="email" className="sr-only">Email address</label>
        <input
          type="email"
          id="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Enter your email"
          required
          className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-lg"
        />
      </div>

      <div className="flex gap-3">
        <button
          type="button"
          onClick={() => setRole('childminder')}
          className={`flex-1 py-3 px-4 rounded-xl border-2 font-medium transition-colors ${
            role === 'childminder'
              ? 'border-primary-500 bg-primary-50 text-primary-700'
              : 'border-gray-200 text-gray-600 hover:border-gray-300'
          }`}
        >
          I'm a Childminder
        </button>
        <button
          type="button"
          onClick={() => setRole('parent')}
          className={`flex-1 py-3 px-4 rounded-xl border-2 font-medium transition-colors ${
            role === 'parent'
              ? 'border-primary-500 bg-primary-50 text-primary-700'
              : 'border-gray-200 text-gray-600 hover:border-gray-300'
          }`}
        >
          I'm a Parent
        </button>
      </div>

      {status === 'error' && (
        <p className="text-red-600 text-sm">{message}</p>
      )}

      <button
        type="submit"
        disabled={status === 'loading'}
        className="w-full py-4 px-6 rounded-xl bg-accent-500 text-white font-semibold text-lg hover:bg-accent-600 transition-colors disabled:opacity-50"
      >
        {status === 'loading' ? 'Joining...' : 'Join the Waitlist'}
      </button>

      <p className="text-center text-sm text-gray-500">
        No spam, ever. Unsubscribe anytime.
      </p>
    </form>
  )
}

export function HomePage() {
  return (
    <div>
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-b from-primary-50 to-white py-20 sm:py-32">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl lg:text-6xl">
              Keep parents connected
              <span className="block text-primary-600">to their child's day</span>
            </h1>
            <p className="mx-auto mt-6 max-w-2xl text-lg text-gray-600">
              Brightseed helps childminders share daily updates, photos, and special moments
              with parents in real-time. Because every moment matters.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/register"
                className="w-full sm:w-auto rounded-full bg-accent-500 px-8 py-4 text-lg font-semibold text-white shadow-lg hover:bg-accent-600 transition-all hover:scale-105"
              >
                Get Started Free
              </Link>
              <Link
                to="/features"
                className="w-full sm:w-auto rounded-full bg-white px-8 py-4 text-lg font-semibold text-gray-700 shadow-md border border-gray-200 hover:bg-gray-50 transition-colors"
              >
                See Features
              </Link>
            </div>
          </div>

          {/* Hero illustration */}
          <div className="mt-16 flex justify-center">
            <div className="relative w-full max-w-3xl">
              <svg
                viewBox="0 0 800 450"
                className="w-full h-auto drop-shadow-2xl"
                xmlns="http://www.w3.org/2000/svg"
              >
                {/* Background */}
                <defs>
                  <linearGradient id="skyGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" stopColor="#dbeafe" />
                    <stop offset="100%" stopColor="#dcfce7" />
                  </linearGradient>
                </defs>

                {/* Sky */}
                <rect width="800" height="450" rx="24" fill="url(#skyGradient)" />

                {/* Sun */}
                <circle cx="680" cy="80" r="45" fill="#fbbf24" />
                <g stroke="#fbbf24" strokeWidth="3" strokeLinecap="round">
                  <line x1="680" y1="20" x2="680" y2="8" />
                  <line x1="680" y1="152" x2="680" y2="140" />
                  <line x1="620" y1="80" x2="608" y2="80" />
                  <line x1="752" y1="80" x2="740" y2="80" />
                  <line x1="640" y1="40" x2="631" y2="31" />
                  <line x1="729" y1="129" x2="720" y2="120" />
                  <line x1="640" y1="120" x2="631" y2="129" />
                  <line x1="729" y1="31" x2="720" y2="40" />
                </g>

                {/* Clouds */}
                <g fill="white" opacity="0.9">
                  <ellipse cx="120" cy="70" rx="45" ry="25" />
                  <ellipse cx="155" cy="62" rx="35" ry="20" />
                  <ellipse cx="95" cy="65" rx="30" ry="18" />

                  <ellipse cx="450" cy="50" rx="40" ry="22" />
                  <ellipse cx="485" cy="45" rx="30" ry="18" />
                  <ellipse cx="425" cy="48" rx="25" ry="16" />
                </g>

                {/* Phone in center */}
                <g transform="translate(300, 100)">
                  {/* Phone body */}
                  <rect x="0" y="0" width="200" height="320" rx="24" fill="#1f2937" />
                  <rect x="8" y="12" width="184" height="296" rx="18" fill="#f8fafc" />

                  {/* Status bar */}
                  <rect x="20" y="24" width="40" height="6" rx="3" fill="#e2e8f0" />
                  <rect x="140" y="24" width="40" height="6" rx="3" fill="#e2e8f0" />

                  {/* App header */}
                  <rect x="20" y="45" width="160" height="35" rx="8" fill="#22c55e" />
                  <circle cx="42" cy="62" r="12" fill="white" opacity="0.3" />
                  <text x="62" y="68" fontSize="14" fill="white" fontWeight="bold" fontFamily="system-ui">Brightseed</text>

                  {/* Update cards */}
                  <g transform="translate(20, 95)">
                    {/* Card 1 */}
                    <rect x="0" y="0" width="160" height="70" rx="10" fill="white" filter="drop-shadow(0 1px 2px rgba(0,0,0,0.1))" />
                    <rect x="10" y="10" width="40" height="40" rx="8" fill="#dcfce7" />
                    <circle cx="30" cy="25" r="10" fill="#86efac" />
                    <path d="M26 25 L29 28 L35 22" stroke="#16a34a" strokeWidth="2" fill="none" strokeLinecap="round" />
                    <rect x="60" y="12" width="90" height="8" rx="4" fill="#22c55e" />
                    <rect x="60" y="26" width="70" height="6" rx="3" fill="#e2e8f0" />
                    <rect x="60" y="38" width="50" height="6" rx="3" fill="#f1f5f9" />
                    <text x="10" y="60" fontSize="8" fill="#9ca3af" fontFamily="system-ui">Just now</text>
                  </g>

                  <g transform="translate(20, 175)">
                    {/* Card 2 with photo */}
                    <rect x="0" y="0" width="160" height="90" rx="10" fill="white" filter="drop-shadow(0 1px 2px rgba(0,0,0,0.1))" />
                    <rect x="10" y="10" width="60" height="45" rx="6" fill="#fef3c7" />
                    <circle cx="25" cy="28" r="8" fill="#fbbf24" />
                    <path d="M35 40 L45 30 L55 38 L65 25" stroke="#f59e0b" strokeWidth="2" fill="none" strokeLinecap="round" />
                    <rect x="80" y="12" width="70" height="8" rx="4" fill="#f97316" />
                    <rect x="80" y="26" width="60" height="6" rx="3" fill="#e2e8f0" />
                    <rect x="80" y="38" width="45" height="6" rx="3" fill="#f1f5f9" />
                    <text x="10" y="75" fontSize="8" fill="#9ca3af" fontFamily="system-ui">2 mins ago</text>
                  </g>

                  {/* Bottom nav */}
                  <rect x="20" y="280" width="160" height="20" rx="10" fill="#f1f5f9" />
                  <circle cx="55" cy="290" r="5" fill="#22c55e" />
                  <circle cx="100" cy="290" r="5" fill="#d1d5db" />
                  <circle cx="145" cy="290" r="5" fill="#d1d5db" />
                </g>

                {/* Notification badges floating */}
                <g transform="translate(520, 140)">
                  <rect x="0" y="0" width="100" height="50" rx="10" fill="white" filter="drop-shadow(0 4px 6px rgba(0,0,0,0.1))" />
                  <circle cx="25" cy="25" r="15" fill="#dcfce7" />
                  <path d="M20 25 L24 29 L32 21" stroke="#22c55e" strokeWidth="2.5" fill="none" strokeLinecap="round" />
                  <rect x="48" y="15" width="42" height="6" rx="3" fill="#e2e8f0" />
                  <rect x="48" y="27" width="32" height="5" rx="2" fill="#f1f5f9" />
                </g>

                <g transform="translate(100, 200)">
                  <rect x="0" y="0" width="90" height="45" rx="10" fill="white" filter="drop-shadow(0 4px 6px rgba(0,0,0,0.1))" />
                  <circle cx="22" cy="22" r="12" fill="#fef3c7" />
                  <circle cx="22" cy="20" r="5" fill="#fbbf24" />
                  <rect x="42" y="12" width="38" height="6" rx="3" fill="#e2e8f0" />
                  <rect x="42" y="24" width="28" height="5" rx="2" fill="#f1f5f9" />
                </g>

                {/* Hearts */}
                <g fill="#f472b6">
                  <path d="M580 280 C580 272 588 268 592 276 C596 268 604 272 604 280 C604 292 592 300 592 300 C592 300 580 292 580 280" />
                  <path d="M150 320 C150 314 156 311 159 317 C162 311 168 314 168 320 C168 329 159 335 159 335 C159 335 150 329 150 320" transform="scale(0.8)" />
                </g>

                {/* Sparkles */}
                <g fill="#fbbf24">
                  <polygon points="560,100 562,106 568,106 564,110 565,116 560,112 555,116 556,110 552,106 558,106" />
                  <polygon points="180,160 181,164 185,164 182,167 183,171 180,168 177,171 178,167 175,164 179,164" />
                  <polygon points="620,220 621,224 625,224 622,227 623,231 620,228 617,231 618,227 615,224 619,224" transform="scale(0.8)" />
                </g>

                {/* Toys at bottom */}
                <g transform="translate(80, 360)">
                  {/* Ball */}
                  <circle cx="30" cy="30" r="25" fill="#f97316" />
                  <path d="M15 20 Q30 10 45 20" stroke="#fb923c" strokeWidth="4" fill="none" />
                  <path d="M15 40 Q30 50 45 40" stroke="#ea580c" strokeWidth="4" fill="none" />
                </g>

                <g transform="translate(620, 350)">
                  {/* Building blocks */}
                  <rect x="0" y="20" width="35" height="35" rx="4" fill="#8b5cf6" />
                  <rect x="40" y="30" width="30" height="25" rx="4" fill="#22c55e" />
                  <rect x="15" y="-5" width="28" height="28" rx="4" fill="#f472b6" />
                </g>

                {/* Crayon */}
                <g transform="translate(700, 380)">
                  <rect x="0" y="0" width="50" height="15" rx="2" fill="#fbbf24" />
                  <polygon points="50,0 65,7.5 50,15" fill="#fbbf24" />
                  <rect x="0" y="0" width="12" height="15" rx="2" fill="#1f2937" />
                </g>
              </svg>
            </div>
          </div>
        </div>
      </section>

      {/* App Store Section */}
      <section className="py-12 bg-white border-b border-gray-100">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="text-gray-600 mb-6">Available on all your devices</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              {/* App Store Button */}
              <div className="relative">
                <div className="flex items-center gap-3 bg-black text-white px-5 py-3 rounded-xl opacity-75">
                  <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                  </svg>
                  <div className="text-left">
                    <div className="text-xs">Download on the</div>
                    <div className="text-lg font-semibold -mt-1">App Store</div>
                  </div>
                </div>
                <span className="absolute -top-2 -right-2 bg-accent-500 text-white text-xs font-semibold px-2 py-1 rounded-full">
                  Coming Soon
                </span>
              </div>

              {/* Play Store Button */}
              <div className="relative">
                <div className="flex items-center gap-3 bg-black text-white px-5 py-3 rounded-xl opacity-75">
                  <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 01-.61-.92V2.734a1 1 0 01.609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.198l2.807 1.626a1 1 0 010 1.73l-2.808 1.626L15.206 12l2.492-2.491zM5.864 2.658L16.8 8.99l-2.302 2.302-8.634-8.634z"/>
                  </svg>
                  <div className="text-left">
                    <div className="text-xs">Get it on</div>
                    <div className="text-lg font-semibold -mt-1">Google Play</div>
                  </div>
                </div>
                <span className="absolute -top-2 -right-2 bg-accent-500 text-white text-xs font-semibold px-2 py-1 rounded-full">
                  Coming Soon
                </span>
              </div>

              {/* Web App */}
              <div className="flex items-center gap-3 bg-primary-600 text-white px-5 py-3 rounded-xl">
                <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418" />
                </svg>
                <div className="text-left">
                  <div className="text-xs">Use on</div>
                  <div className="text-lg font-semibold -mt-1">Web Browser</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Problem/Solution Section */}
      <section className="py-20 bg-white">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block px-4 py-1 rounded-full bg-red-100 text-red-700 text-sm font-medium mb-4">
                The Problem
              </span>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Parents miss out on precious moments
              </h2>
              <p className="text-gray-600 text-lg">
                Working parents often feel disconnected from their child's daily experiences.
                They wonder what their little ones did, learned, and how they felt throughout the day.
              </p>
            </div>
            <div>
              <span className="inline-block px-4 py-1 rounded-full bg-green-100 text-green-700 text-sm font-medium mb-4">
                The Solution
              </span>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Real-time updates from their childminder
              </h2>
              <p className="text-gray-600 text-lg">
                Brightseed bridges the gap by enabling childminders to quickly share updates,
                photos, and activities. Parents stay connected and never miss a moment.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Key Features Section */}
      <section className="py-20 bg-gray-50">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 sm:text-4xl">
              Everything you need to stay connected
            </h2>
            <p className="mt-4 text-lg text-gray-600">
              Simple tools that make sharing easy and delightful
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Feature 1 */}
            <div className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-12 h-12 rounded-xl bg-primary-100 flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Daily Updates</h3>
              <p className="text-gray-600 text-sm">
                Quick text updates about activities, meals, naps, and more.
              </p>
            </div>

            {/* Feature 2 */}
            <div className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Attendance Tracking</h3>
              <p className="text-gray-600 text-sm">
                Check children in and out with arrival and departure times.
              </p>
            </div>

            {/* Feature 3 */}
            <div className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Shared Calendar</h3>
              <p className="text-gray-600 text-sm">
                Parents report holidays and sick days. Plan events together.
              </p>
            </div>

            {/* Feature 4 */}
            <div className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-12 h-12 rounded-xl bg-accent-100 flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-accent-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Photo Sharing</h3>
              <p className="text-gray-600 text-sm">
                Capture and share special moments with easy photo uploads.
              </p>
            </div>

            {/* Feature 5 */}
            <div className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Secure Messaging</h3>
              <p className="text-gray-600 text-sm">
                Direct chat between childminders and parents. All messages encrypted.
              </p>
            </div>
          </div>

          <div className="text-center mt-12">
            <Link
              to="/features"
              className="inline-flex items-center text-primary-600 font-semibold hover:text-primary-700"
            >
              See all features
              <svg className="ml-2 w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 bg-white">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 sm:text-4xl">
              How it works
            </h2>
            <p className="mt-4 text-lg text-gray-600">
              Get started in minutes, not hours
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {/* Step 1 */}
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-primary-600 text-white font-bold text-xl flex items-center justify-center mx-auto mb-4">
                1
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Sign up</h3>
              <p className="text-gray-600 text-sm">
                Create your free account as a childminder or parent
              </p>
            </div>

            {/* Step 2 */}
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-primary-600 text-white font-bold text-xl flex items-center justify-center mx-auto mb-4">
                2
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Add children</h3>
              <p className="text-gray-600 text-sm">
                Childminders create profiles for each child in their care
              </p>
            </div>

            {/* Step 3 */}
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-primary-600 text-white font-bold text-xl flex items-center justify-center mx-auto mb-4">
                3
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Invite parents</h3>
              <p className="text-gray-600 text-sm">
                Send simple email invitations to connect with parents
              </p>
            </div>

            {/* Step 4 */}
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-primary-600 text-white font-bold text-xl flex items-center justify-center mx-auto mb-4">
                4
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Start sharing</h3>
              <p className="text-gray-600 text-sm">
                Share updates and photos throughout the day
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-primary-50">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 sm:text-4xl">
              Loved by families
            </h2>
            <p className="mt-4 text-lg text-gray-600">
              See what parents and childminders are saying
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Testimonial 1 */}
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <div className="flex items-center gap-1 text-yellow-400 mb-4">
                {[...Array(5)].map((_, i) => (
                  <svg key={i} className="w-5 h-5 fill-current" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                ))}
              </div>
              <p className="text-gray-600 mb-4">
                "As a working mum, I used to feel so disconnected. Now I get lovely updates
                throughout the day. It's made such a difference!"
              </p>
              <p className="font-semibold text-gray-900">Sarah M.</p>
              <p className="text-gray-500 text-sm">Parent</p>
            </div>

            {/* Testimonial 2 */}
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <div className="flex items-center gap-1 text-yellow-400 mb-4">
                {[...Array(5)].map((_, i) => (
                  <svg key={i} className="w-5 h-5 fill-current" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                ))}
              </div>
              <p className="text-gray-600 mb-4">
                "So easy to use! I can snap a photo and send an update in seconds.
                Parents love it and it's become part of my daily routine."
              </p>
              <p className="font-semibold text-gray-900">Emma T.</p>
              <p className="text-gray-500 text-sm">Childminder</p>
            </div>

            {/* Testimonial 3 */}
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <div className="flex items-center gap-1 text-yellow-400 mb-4">
                {[...Array(5)].map((_, i) => (
                  <svg key={i} className="w-5 h-5 fill-current" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                ))}
              </div>
              <p className="text-gray-600 mb-4">
                "The peace of mind is priceless. Getting a photo of my son playing
                happily at lunchtime makes my whole day better."
              </p>
              <p className="font-semibold text-gray-900">James K.</p>
              <p className="text-gray-500 text-sm">Parent</p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Preview Section */}
      <section className="py-20 bg-white">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 sm:text-4xl">
              Simple, transparent pricing
            </h2>
            <p className="mt-4 text-lg text-gray-600">
              No hidden fees. Cancel anytime.
            </p>
          </div>

          <div className="max-w-lg mx-auto bg-gradient-to-br from-primary-50 to-accent-50 rounded-3xl p-8 shadow-lg">
            <div className="text-center">
              <p className="text-gray-600 mb-2">For Childminders</p>
              <div className="flex items-baseline justify-center gap-1">
                <span className="text-5xl font-bold text-gray-900">£2</span>
                <span className="text-gray-600">/month per child</span>
              </div>
              <p className="mt-4 text-gray-600">
                Parents always free. Childminders pay per child profile.
              </p>
              <Link
                to="/pricing"
                className="inline-block mt-6 text-primary-600 font-semibold hover:text-primary-700"
              >
                View full pricing details →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Email Capture Section */}
      <section className="py-20 bg-gray-50">
        <div className="mx-auto max-w-xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <span className="inline-block px-4 py-1 rounded-full bg-accent-100 text-accent-700 text-sm font-medium mb-4">
              Get Early Access
            </span>
            <h2 className="text-3xl font-bold text-gray-900 sm:text-4xl">
              Join the waitlist
            </h2>
            <p className="mt-4 text-lg text-gray-600">
              Be the first to know when we launch new features. Founding members get extended free access.
            </p>
          </div>

          <EmailCapture />
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 bg-gradient-to-br from-primary-600 to-primary-700">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white sm:text-4xl">
            Ready to get started?
          </h2>
          <p className="mt-4 text-lg text-primary-100 max-w-2xl mx-auto">
            Join childminders and parents who use Brightseed to stay connected.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/register"
              className="w-full sm:w-auto rounded-full bg-white px-8 py-4 text-lg font-semibold text-primary-600 shadow-lg hover:bg-gray-50 transition-colors"
            >
              Create Free Account
            </Link>
            <Link
              to="/login"
              className="w-full sm:w-auto rounded-full border-2 border-white px-8 py-4 text-lg font-semibold text-white hover:bg-white/10 transition-colors"
            >
              Sign In
            </Link>
          </div>
        </div>
      </section>
    </div>
  )
}
